/**
 * CampaignChronicleView — accepted chronicle entries grouped by session.
 *
 * Phase 7a — turns the per-meeting review pane (where DM/players triage
 * proposals) into a long-running campaign timeline (where the accepted canon
 * lives). Read-only here: editing/rejecting an already-accepted entry happens
 * via the meeting page's review pane (or the per-character journal view).
 *
 * The smart wrapper here fans out four React Query calls (chronicle entries
 * + characters + NPCs + locations); CampaignChronicleViewView holds the
 * actual timeline rendering.
 */

import { useCompanyQuery } from "../../hooks/useCompanyQuery"
import { fetchCharacters, fetchChronicleEntries, fetchLocations, fetchNpcs } from "../../lib/api"
import { queryKeys } from "../../lib/query-keys"
import { CampaignChronicleViewView } from "./CampaignChronicleViewView"
import type { ICharacterRoster, IWorldLocation, IWorldNpc } from "./CampaignChronicleViewView/interfaces"
import type { IProps } from "./interfaces"

export function CampaignChronicleView({ seriesId }: IProps): React.JSX.Element {
  const chronicleQuery = useCompanyQuery({
    key: (org) => queryKeys.campaignChronicleAccepted(org, seriesId),
    queryFn: (company) => fetchChronicleEntries(company, seriesId, { status: "accepted" }),
  })

  const charactersQuery = useCompanyQuery({
    key: (org) => queryKeys.canonCharacters(org, seriesId),
    queryFn: (company) => fetchCharacters(company, seriesId),
  })

  // World-state lookups for chip linkback (Phase WS-8). Same query keys the
  // NPCs / Locations tabs use so React Query dedupes; the chronicle view
  // doesn't trigger extra network calls when those tabs have already loaded.
  const npcsQuery = useCompanyQuery({
    key: (org) => queryKeys.worldNpcs(org, seriesId),
    queryFn: (company) => fetchNpcs(company, seriesId),
  })

  const locationsQuery = useCompanyQuery({
    key: (org) => queryKeys.worldLocations(org, seriesId),
    queryFn: (company) => fetchLocations(company, seriesId),
  })

  const characters = (charactersQuery.data ?? []) as ICharacterRoster[]
  const npcs = (npcsQuery.data ?? []) as IWorldNpc[]
  const locations = (locationsQuery.data ?? []) as IWorldLocation[]

  return (
    <CampaignChronicleViewView
      seriesId={seriesId}
      entries={chronicleQuery.data ?? []}
      characters={characters}
      npcs={npcs}
      locations={locations}
      isLoading={chronicleQuery.isLoading}
    />
  )
}
